import { useState } from "react";
import {
    Container,
    Box,
    Typography,
    Divider,
    Autocomplete,
    TextField,
    CircularProgress,
    Button,
    Stack,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import useStationSearch from "../hooks/useStationSearch";
import { setDepartureStation } from "../redux/slices/searchSlice";
import {
    getDefaultStation,
    setDefaultStation,
    clearDefaultStation,
} from "../services/defaultStation";

export default function SettingsPage() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [station, setStation] = useState(() => getDefaultStation());
    const { stations, loading, input, setInput } = useStationSearch();

    const handleSelect = (value) => {
        setStation(value);
        if (value) {
            setDefaultStation(value);
            // odmah je i aktivna polazna u formularu pretrage
            dispatch(setDepartureStation(value));
            enqueueSnackbar(t("settings.saved"), { variant: "success" });
        }
    };

    const handleClear = () => {
        setStation(null);
        clearDefaultStation();
        enqueueSnackbar(t("settings.cleared"), { variant: "info" });
    };

    return (
        <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "grey.100", py: 4 }}>
            <Container maxWidth="sm">
                <Box sx={{ bgcolor: "white", borderRadius: 3, p: { xs: 3, md: 5 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                        {t("settings.title")}
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {t("settings.defaultStationTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t("settings.defaultStationDesc")}
                    </Typography>

                    <Autocomplete
                        fullWidth
                        options={stations}
                        getOptionLabel={(option) => option?.name || ""}
                        value={station}
                        onChange={(_, value) => handleSelect(value)}
                        onInputChange={(_, value, reason) => {
                            if (reason === "input") setInput(value);
                        }}
                        loading={loading}
                        filterOptions={(x) => x}
                        noOptionsText={input.length < 2 ? t("home.minTwoChars") : t("home.noStations")}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("home.fromShort")}
                                placeholder={t("home.stationPlaceholder")}
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <Icon
                                                icon="mdi:map-marker-radius"
                                                width={20}
                                                color="#1a1a2e"
                                                style={{ marginRight: 6 }}
                                            />
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                    endAdornment: (
                                        <>
                                            {loading ? <CircularProgress size={18} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {station && (
                        <Stack direction="row" justifyContent="flex-start" sx={{ mt: 2 }}>
                            <Button
                                color="error"
                                size="small"
                                startIcon={<Icon icon="mdi:close-circle-outline" width={18} />}
                                onClick={handleClear}
                            >
                                {t("settings.defaultStationClear")}
                            </Button>
                        </Stack>
                    )}
                </Box>
            </Container>
        </Box>
    );
}
